import type { IntelArchiveState } from '../model/intel-archive.js';
import { criticalityOf } from '../model/intel.js';
import { DATA, DATA_TAG } from '../../theme/type.js';

/**
 * The resolution-log archive (design 470–723, M5 Q18).
 *
 * One row per **resolved** turn, newest first: `1 … currentTurn − 1`, because the
 * route 404s anything at or above the turn being played and a rail of rows that
 * would all refuse is not a rail. On turn 1 it is empty rather than a list of one
 * apology.
 *
 * **No stamp.** The prototype draws `1420Z 21 MAR` per report and there is no
 * such thing on this wire — no clock, no date, no wall time. The turn is the only
 * time this console has, so the turn is the row.
 *
 * ## Five states, and none of them looks like another
 *
 * Every row carries one, and the two the brief is most concerned about are the
 * two furthest apart: a turn whose report is **fetched and empty** says `0`, and
 * a turn that has **not been fetched** says nothing at all. See
 * `model/intel-archive.ts` for the table.
 *
 * ## Nothing is fetched by scrolling
 *
 * Rows are `idle` until opened. A rail that fetched every turn on mount would put
 * *n* REST calls behind one tab switch, each of which rebuilds a report from
 * events server-side — and it would do it for turns nobody reads. The reader
 * requests the turn it is showing, and the delta requests the one before it, and
 * that is all.
 */

export const INTEL_RAIL_WIDTH = 190;

export interface ArchiveRailProps {
  readonly turns: readonly number[];
  readonly selected: number | null;
  readonly stateOf: (turn: number) => IntelArchiveState;
  readonly onSelect: (turn: number) => void;
}

export function ArchiveRail(props: ArchiveRailProps): JSX.Element {
  return (
    <div
      data-testid="intel-archive"
      data-mat="panel"
      style={{
        flex: 'none',
        width: INTEL_RAIL_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
      }}
    >
      <div
        data-mat="chrome hdr"
        style={{
          ...DATA_TAG,
          flex: 'none',
          height: 33,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
          fontSize: 10.5,
          color: 'var(--text-muted)',
        }}
      >
        ARCHIVE
      </div>

      {/* No internal scroll: the centre pane's column carries the whole tab, and
          a rail that scrolled inside a page that also scrolls is two scrollbars
          for one list. */}
      <div style={{ flex: 'none' }}>
        {props.turns.map((turn) => {
          const state = props.stateOf(turn);
          const selected = turn === props.selected;
          return (
            <button
              key={turn}
              type="button"
              data-testid="intel-archive-row"
              data-turn={turn}
              data-status={state.status}
              aria-current={selected ? 'true' : undefined}
              onClick={() => props.onSelect(turn)}
              style={{
                ...DATA,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 12px',
                height: 28,
                background: selected ? 'var(--accent-tint)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--rule-faint)',
                borderLeft: '2px solid ' + (selected ? 'var(--accent-border)' : 'transparent'),
                color: selected ? 'var(--text)' : 'var(--text-2)',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span style={{ flex: 'none' }}>TURN {String(turn).padStart(2, '0')}</span>
              <span style={{ flex: 1 }} />
              <RowState state={state} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RowState(props: { state: IntelArchiveState }): JSX.Element | null {
  const { state } = props;
  switch (state.status) {
    // Not asked for. Nothing is drawn, because there is nothing known — and a
    // dash here would be the rail claiming an answer it has not got.
    case 'idle':
      return null;
    case 'loading':
      return <Tail testId="intel-row-loading" colour="var(--text-muted)" text="…" />;
    case 'ready': {
      const critical = state.report.entries.filter(
        (entry) => criticalityOf(entry.type) === 'CRITICAL',
      ).length;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {critical > 0 && (
            <span
              data-testid="intel-row-critical"
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: 'var(--red-text)',
              }}
            />
          )}
          {/* `0` is the point of this rail: a resolved turn in which nothing
              happened to this seat is an empty report, not a missing one. */}
          <span data-testid="intel-row-count" style={{ color: 'var(--text-muted)' }}>
            {state.report.entries.length}
          </span>
        </span>
      );
    }
    case 'absent':
      return <Tail testId="intel-row-absent" colour="var(--text-muted)" text="NOT RESOLVED" />;
    case 'forbidden':
      return <Tail testId="intel-row-forbidden" colour="var(--text-muted)" text="NO REPORT" />;
    case 'failed':
      return <Tail testId="intel-row-failed" colour="var(--warn)" text={state.reason} />;
  }
}

function Tail(props: { testId: string; colour: string; text: string }): JSX.Element {
  return (
    <span
      data-testid={props.testId}
      style={{ ...DATA_TAG, flex: 'none', fontSize: 9.5, color: props.colour }}
    >
      {props.text}
    </span>
  );
}
